/* eslint-disable fp/no-mutation */
import { match } from '@frontend/duck-tape';
import { useCallback, useEffect, useRef, useState } from 'react';

export type UseLoadingTimeoutsProps = {
  backoffMultiplier?: number;
  backoffStrategy?: 'constant' | 'exponential' | 'linear';
  isLoading: boolean;
  maxRetries?: number;
  onRefetchTimeout: () => Promise<void>;
  onShowButtonTimeout: () => void;
  refetchTimeoutMs?: number;
  shouldTrigger: boolean;
  showButtonTimeoutMs?: number;
};

export const useLoadingTimeouts = ({
  backoffMultiplier = 1.5,
  backoffStrategy = 'linear',
  isLoading,
  maxRetries = 3,
  onRefetchTimeout,
  onShowButtonTimeout,
  refetchTimeoutMs = 5000,
  shouldTrigger,
  showButtonTimeoutMs = 10000,
}: UseLoadingTimeoutsProps) => {
  const [retryCount, setRetryCount] = useState(0);
  const loadingSessionIdRef = useRef(0);

  // Use refs to avoid stale closure issues
  const onRefetchTimeoutRef = useRef(onRefetchTimeout);
  const onShowButtonTimeoutRef = useRef(onShowButtonTimeout);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onRefetchTimeoutRef.current = onRefetchTimeout;
    onShowButtonTimeoutRef.current = onShowButtonTimeout;
  }, [onRefetchTimeout, onShowButtonTimeout]);

  const retriesExceeded = retryCount >= maxRetries;
  const shouldStartTimeouts = isLoading && shouldTrigger;

  // Calculate timeout based on backoff strategy
  const calculateTimeout = useCallback(
    (retryIndex: number) => {
      return match(backoffStrategy)
        .with('linear', () => refetchTimeoutMs * (1 + retryIndex * (backoffMultiplier - 1)))
        .with('exponential', () => refetchTimeoutMs * Math.pow(backoffMultiplier, retryIndex))
        .with('constant', () => refetchTimeoutMs)
        .exhaustive();
    },
    [backoffStrategy, backoffMultiplier, refetchTimeoutMs],
  );

  // Schedule a refetch with configurable backoff
  const scheduleRefetch = useCallback(
    (currentSessionId: number, currentRetryCount: number) => {
      if (currentRetryCount >= maxRetries) return;

      const timeoutMs = calculateTimeout(currentRetryCount);

      setTimeout(() => {
        // Only execute if this is still the current loading session
        if (loadingSessionIdRef.current === currentSessionId) {
          const newRetryCount = currentRetryCount + 1;
          setRetryCount(newRetryCount);
          // Schedule next retry if still under limit
          onRefetchTimeoutRef.current().then(() => {
            scheduleRefetch(currentSessionId, newRetryCount);
          });
        }
      }, timeoutMs);
    },
    [maxRetries, calculateTimeout],
  );

  // Main effect that only runs when loading state changes
  useEffect(() => {
    if (shouldStartTimeouts) {
      // Start new loading session
      const sessionId = loadingSessionIdRef.current + 1;
      loadingSessionIdRef.current = sessionId;
      setRetryCount(0);

      // Schedule first refetch
      scheduleRefetch(sessionId, 0);

      // Schedule button timeout (only once per session)
      setTimeout(() => {
        if (loadingSessionIdRef.current === sessionId) {
          onShowButtonTimeoutRef.current();
        }
      }, showButtonTimeoutMs);
    } else {
      // Loading stopped - invalidate current session
      loadingSessionIdRef.current = loadingSessionIdRef.current + 1;
      setRetryCount(0);
    }
  }, [shouldStartTimeouts, scheduleRefetch, showButtonTimeoutMs]);

  const manualRefetch = useCallback(() => {
    setRetryCount(0); // Reset retry count for manual attempts
    onRefetchTimeoutRef.current();
  }, []);

  return {
    manualRefetch,
    retriesExceeded,
    retryCount,
  };
};
